<script setup>
import { ref, reactive, computed, nextTick, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRouter } from 'vue-router';
import axios from '../../axios';
import { useTranslationStore } from '@/stores/translation';
import { useCartStore } from '@/stores/cart';
import { useAuthStore } from '@/stores/auth';
import { useWorkingDaysStore } from '@/stores/workingdays';
import { useWeeklyMenuStore } from '@/stores/weeklyMenu';

const tr = useTranslationStore();
const cart = useCartStore();
const wk = useWeeklyMenuStore();
const weeklyOn = computed(() => !!wk.enabled);
function weeklySnapshot() { try { return weeklyOn.value ? { deliveries: wk.payload(), active_date: wk.activeDate } : null; } catch (e) { return null; } }
const workingDays = useWorkingDaysStore();
const router = useRouter();
// Home & OrderNow render a full-width fixed bottom bar (cart / order) on tablet+mobile;
// lift the chat launcher above it on those routes so the two never overlap.
const onBarPage = computed(() => ['Home', 'Ordernow'].includes(router.currentRoute.value && router.currentRoute.value.name));

const cfg = ref(null);
const open = ref(false);
// Hide the whole widget while any Bootstrap modal/offcanvas (login, signup, cart
// drawer…) is open — they fight the chat for focus + stacking, esp. on mobile.
const bsOverlays = ref(0);
function bsOverlayShow() { bsOverlays.value += 1; }
function bsOverlayHide() { bsOverlays.value = Math.max(0, bsOverlays.value - 1); }
onMounted(() => {
  document.addEventListener('show.bs.modal', bsOverlayShow);
  document.addEventListener('hidden.bs.modal', bsOverlayHide);
  document.addEventListener('show.bs.offcanvas', bsOverlayShow);
  document.addEventListener('hidden.bs.offcanvas', bsOverlayHide);
});
onBeforeUnmount(() => {
  document.removeEventListener('show.bs.modal', bsOverlayShow);
  document.removeEventListener('hidden.bs.modal', bsOverlayHide);
  document.removeEventListener('show.bs.offcanvas', bsOverlayShow);
  document.removeEventListener('hidden.bs.offcanvas', bsOverlayHide);
});
const sending = ref(false);
const streamingActive = ref(false);   // a reply is currently streaming into a bubble
const input = ref('');
const messages = ref([]);         // { role, text?, cards?, quick? }
const sessionKey = ref('');
const scroller = ref(null);
const addedIds = ref({});         // portion_price_id -> true (for the ✓ state)
const teaser = ref(false);        // the proactive pop-up next to the bubble
// Human handoff: capture contact details from a guest so a person can follow up.
const askContact = ref(false);
const contactSent = ref(false);
const contactSending = ref(false);
const contactErr = ref('');
const contact = reactive({ name: '', phone: '', email: '' });
const CONTACT_LS = 'chds_chat_contact_sent';
const TEASER_LS = 'chds_chat_teaser_seen';

const lang = computed(() => (tr.currentLanguage === 'zh' ? 'zh' : 'en'));
const enabled = computed(() => !!cfg.value && cfg.value.enabled);
const botName = computed(() => (cfg.value && cfg.value.bot_name) || 'CHDS');
const chips = computed(() =>
  ((cfg.value && cfg.value.suggested_questions) || [])
    .map((q) => (lang.value === 'zh' ? q.zh || q.en : q.en || q.zh)).filter(Boolean));
const teaserText = computed(() => (cfg.value ? (lang.value === 'zh' ? cfg.value.teaser_zh : cfg.value.teaser_en) : ''));

const SESSION_LS = 'chds_chat_session';
const MSGS_LS = 'chds_chat_msgs';
const OPEN_LS = 'chds_chat_open';
const ADDED_LS = 'chds_chat_added';
const ACTIVE_LS = 'chds_chat_active';
const STALE_MS = 30 * 60 * 1000;   // after 30 min away, offer resume-or-new

const resumable = ref(false);      // a previous chat exists but is stale → offer to resume
const stashed = ref([]);           // the stashed previous conversation

// Persist the conversation so it survives page navigation / reloads.
function persist() {
  try {
    localStorage.setItem(MSGS_LS, JSON.stringify(messages.value.slice(-40)));
    localStorage.setItem(OPEN_LS, open.value ? '1' : '0');
    localStorage.setItem(ADDED_LS, JSON.stringify(addedIds.value));
    localStorage.setItem(ACTIVE_LS, String(Date.now()));
  } catch (e) { /* noop */ }
}

onMounted(async () => {
  syncViewport();
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', syncViewport);
    window.visualViewport.addEventListener('scroll', syncViewport);
  }
  window.addEventListener('resize', syncViewport);   // fallback for no-visualViewport browsers
  try { sessionKey.value = localStorage.getItem(SESSION_LS) || ''; } catch (e) { /* noop */ }
  try { contactSent.value = localStorage.getItem(CONTACT_LS) === '1'; } catch (e) { /* noop */ }
  // Restore an in-progress conversation. If the customer's been away a while,
  // stash it and offer "continue vs. new" instead of dumping the old thread.
  try {
    const saved = JSON.parse(localStorage.getItem(MSGS_LS) || 'null');
    const lastActive = parseInt(localStorage.getItem(ACTIVE_LS) || '0', 10);
    const savedAdded = JSON.parse(localStorage.getItem(ADDED_LS) || 'null');
    if (savedAdded && typeof savedAdded === 'object') addedIds.value = savedAdded;
    if (Array.isArray(saved) && saved.length) {
      if (Date.now() - lastActive > STALE_MS) {
        stashed.value = saved;          // hold it — show a resume prompt instead
        resumable.value = true;
      } else {
        messages.value = saved;         // recent — restore inline
        if (localStorage.getItem(OPEN_LS) === '1') open.value = true;
      }
    }
  } catch (e) { /* noop */ }
  try { cfg.value = (await axios.get('/chat/config/')).data; } catch (e) { cfg.value = { enabled: false }; }
  // If restored open with no history yet, greet now.
  if (open.value && !messages.value.length) { greet(); scrollSoon(); }
  if (open.value && isMobile()) lockBodyScroll();
  // Proactive teaser: invite first-time visitors to chat (once — until they act).
  let seen = false;
  try { seen = localStorage.getItem(TEASER_LS) === '1'; } catch (e) { /* noop */ }
  if (enabled.value && teaserText.value && !open.value && !seen && !messages.value.length) {
    setTimeout(() => { if (!open.value) teaser.value = true; }, 4200);
  }
});

function markTeaserSeen() { try { localStorage.setItem(TEASER_LS, '1'); } catch (e) { /* noop */ } }
function dismissTeaser() { teaser.value = false; markTeaserSeen(); }
function openFromTeaser() { teaser.value = false; markTeaserSeen(); if (!open.value) toggle(); }

// Lock the page behind the full-screen chat on mobile, so scrolling the chat
// history doesn't scroll the page underneath (iOS-safe position:fixed method).
let scrollLockY = 0;
function isMobile() { return typeof window !== 'undefined' && window.innerWidth <= 560; }
function lockBodyScroll() {
  if (document.body.dataset.chwLocked === '1') return;
  scrollLockY = window.scrollY || window.pageYOffset || 0;
  const b = document.body;
  b.dataset.chwLocked = '1';
  b.style.position = 'fixed'; b.style.top = `-${scrollLockY}px`;
  b.style.left = '0'; b.style.right = '0'; b.style.width = '100%';
}
function unlockBodyScroll() {
  const b = document.body;
  if (b.dataset.chwLocked !== '1') return;
  delete b.dataset.chwLocked;
  b.style.position = ''; b.style.top = ''; b.style.left = ''; b.style.right = ''; b.style.width = '';
  window.scrollTo(0, scrollLockY);
}
watch(open, (o) => { if (o && isMobile()) lockBodyScroll(); else unlockBodyScroll(); });
// If a Bootstrap modal/offcanvas opens over an open chat, the widget is v-show-hidden
// but `open` stays true — so OUR body lock stayed applied while Bootstrap also took
// over the body, compounding into broken scroll when the overlay closed. Hand the
// body over while an overlay is up; re-lock when it clears and the chat is still open.
watch(bsOverlays, (n) => {
  if (n > 0) unlockBodyScroll();
  else if (open.value && isMobile()) lockBodyScroll();
});

// Keep the full-screen mobile panel pinned to the VISUAL viewport. The on-screen
// keyboard both SHRINKS the visual viewport (height) and can OFFSET it (iOS scrolls
// the page so the focused input is visible — visualViewport.offsetTop). A top:0
// panel sized only by height drifts out of alignment and the page behind "bleeds
// through"; tracking BOTH height and offsetTop keeps the chat glued to what the
// customer actually sees.
function syncViewport() {
  try {
    const vv = window.visualViewport;
    const h = vv ? vv.height : window.innerHeight;
    const t = vv ? (vv.offsetTop || 0) : 0;
    document.documentElement.style.setProperty('--chw-vh', h + 'px');
    document.documentElement.style.setProperty('--chw-top', t + 'px');
  } catch (e) { /* noop */ }
}
onBeforeUnmount(() => {
  unlockBodyScroll();
  if (window.visualViewport) {
    window.visualViewport.removeEventListener('resize', syncViewport);
    window.visualViewport.removeEventListener('scroll', syncViewport);
  }
  window.removeEventListener('resize', syncViewport);
});

function greet() {
  if (messages.value.length || resumable.value) return;
  const g = lang.value === 'zh' ? cfg.value.greeting_zh : cfg.value.greeting_en;
  if (g) { messages.value.push({ role: 'assistant', text: g }); persist(); }
}
function resumeChat() {
  messages.value = stashed.value; stashed.value = [];
  resumable.value = false; persist(); scrollSoon();
}
function newChat() {
  stashed.value = []; resumable.value = false; messages.value = []; addedIds.value = {};
  try { localStorage.removeItem(MSGS_LS); localStorage.removeItem(ADDED_LS); localStorage.removeItem(SESSION_LS); } catch (e) { /* noop */ }
  sessionKey.value = ''; greet(); scrollSoon();
}
function toggle() { open.value = !open.value; if (open.value) { teaser.value = false; markTeaserSeen(); greet(); scrollSoon(); } else { cartView.value = false; } persist(); }

// Match the customer's language: if only the greeting is showing and the site
// language changes (auto-detected or toggled), swap the greeting to that language.
watch(lang, (nl) => {
  if (!cfg.value) return;
  if (messages.value.length === 1 && messages.value[0].role === 'assistant' && !messages.value[0].cards) {
    messages.value = [{ role: 'assistant', text: nl === 'zh' ? cfg.value.greeting_zh : cfg.value.greeting_en }];
    persist();
  }
});
// Pin the history to the bottom — throttled to one write per frame. During streaming
// this used to fire on EVERY token; combined with keyboard viewport resizes on mobile
// the flood of scrollTop writes made the whole panel visibly jitter.
let scrollQueued = false;
function scrollSoon() {
  if (scrollQueued) return;
  scrollQueued = true;
  nextTick(() => requestAnimationFrame(() => {
    scrollQueued = false;
    if (scroller.value) scroller.value.scrollTop = scroller.value.scrollHeight;
  }));
}

// Safe minimal markdown: escape, then **bold**, *italic*, line breaks.
function render(t) {
  const esc = (t || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return esc
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*(?!\s)(.+?)\*(?!\*)/g, '$1<em>$2</em>')
    .replace(/\n/g, '<br>');
}

function addVariant(card, v) {
  if (card.weekly && card.date) {
    wk.activeDate = card.date;
    wk.addLine(card.date, { kind: 'item', variant_id: v.portion_price_id, name: card.name, trans_code: card.trans_code,
      portion: v.portion.portion_name, weight: v.portion.portion_weight, price: v.portion.price, qty: 1, rice: (v.is_meal_set ? 'white' : null) });
    addedIds.value = { ...addedIds.value, [v.portion_price_id]: true };
    persist();
    return;
  }
  cart.resetActive();
  cart.updateActivePortion(v.portion);
  cart.updateActiveQuantity(1);
  cart.addToCart({ name: card.name, id: card.id, trans_code: card.trans_code });
  addedIds.value = { ...addedIds.value, [v.portion_price_id]: true };
  persist();
}

/** The basket, as the bot needs to see it. Only ids + quantities go up — the server
 *  re-prices every line from the catalogue, so client prices are never trusted. */
function cartSnapshot() {
  try {
    return (cart.cart || []).slice(0, 60).map((l) => ({
      selected_meal_portion_id: l.selected_meal_portion_id,
      quantity: l.quantity,
      meal_name: l.meal_name,
    }));
  } catch (e) { return []; }
}

// What the bot has staged for checkout (date / slot / order type). Kept here and echoed
// back to the server each turn so it survives page navigation without a DB write.
const DELIVERY_LS = 'chds_bot_delivery_v1';
function readDelivery() {
  try { return JSON.parse(localStorage.getItem(DELIVERY_LS) || '{}') || {}; } catch (e) { return {}; }
}
function writeDelivery(d) {
  try { localStorage.setItem(DELIVERY_LS, JSON.stringify(d || {})); } catch (e) { /* noop */ }
}

/** Pre-fill the checkout page. CheckoutPage.restoreFormData() reads this blob on mount
 *  (it even handles the flatpickr date dance), so we MERGE into it rather than replace —
 *  overwriting would wipe a delivery address the customer had already chosen. */
function stageCheckout(d) {
  if (!d) return;
  let blob = {};
  try { blob = JSON.parse(localStorage.getItem('checkoutFormData') || '{}') || {}; } catch (e) { blob = {}; }
  if (d.order_type) blob.selectedOption = String(d.order_type).toLowerCase() === 'pickup' ? 'pickup' : 'delivery';
  if (d.schedule_date) blob.schedule_date = d.schedule_date;
  if (d.time_slot) blob.schedule_time = d.time_slot;
  try { localStorage.setItem('checkoutFormData', JSON.stringify(blob)); } catch (e) { /* noop */ }
  // Also set the stores directly, so a checkout page already open updates immediately.
  try {
    if (d.schedule_date) workingDays.setScheduleDate(d.schedule_date);
    if (d.time_slot) workingDays.setScheduleTime(d.time_slot);
  } catch (e) { /* noop */ }
}

function applyActions(actions) {
  for (const a of actions || []) {
    if (a.type === 'add_to_weekly_basket' && a.date && a.variant_id) {
      wk.activeDate = a.date;
      wk.addLine(a.date, { kind: 'item', variant_id: a.variant_id, name: a.name, trans_code: a.trans_code, portion: a.portion,
        weight: a.weight, price: a.price, qty: a.qty || 1, rice: a.rice || null });
      addedIds.value = { ...addedIds.value, [a.variant_id]: true };
    } else if (a.type === 'set_weekly_date' && a.date) {
      wk.activeDate = a.date;
      wk.setDeliveryDetails(a.date, { ...(a.time_slot ? { time_slot: a.time_slot } : {}), ...(a.order_type ? { order_type: a.order_type } : {}) });
    } else if (a.type === 'go_checkout' && a.path) {
      cartView.value = false; navTo(a.path);
    } else if (a.type === 'add_to_cart' && a.portion && a.dish) {
      cart.resetActive();
      cart.updateActivePortion(a.portion);
      cart.updateActiveQuantity(a.qty || 1);
      cart.addToCart({ name: a.dish.name, id: a.dish.id, trans_code: a.dish.trans_code });
    } else if (a.type === 'set_delivery' && a.delivery) {
      writeDelivery(a.delivery);
      stageCheckout(a.delivery);
    } else if (a.type === 'go_checkout') {
      if (a.delivery) { writeDelivery(a.delivery); stageCheckout(a.delivery); }
      goCheckout();
    }
  }
}

function rememberSession(key) {
  if (!key) return;
  sessionKey.value = key;
  try { localStorage.setItem(SESSION_LS, key); } catch (e) { /* noop */ }
}

// Attach a completed reply's cards/chips/actions and finish the turn. Shared by the
// streaming `final` handler and the non-stream fallback.
function finishReply(bubble, data) {
  rememberSession(data.session_key);
  applyActions(data.actions);
  if (bubble) {
    bubble.text = data.reply != null ? data.reply : bubble.text;
    bubble.cards = data.cards || [];
    bubble.quick = data.quick_replies || [];
    bubble.streaming = false;
  } else {
    messages.value.push({ role: 'assistant', text: data.reply, cards: data.cards || [], quick: data.quick_replies || [] });
  }
  if (data.ask_contact && !contactSent.value) askContact.value = true;
}

function errorReply(busy) {
  messages.value.push({
    role: 'assistant',
    text: lang.value === 'zh'
      ? (busy ? '消息太快啦，请稍等一下 🙏' : '不好意思，出了点问题，请稍后再试。')
      : (busy ? "You're chatting fast — one moment please 🙏" : 'Sorry, something went wrong. Please try again.'),
  });
}

// Non-streaming fallback — the original one-shot request.
async function sendBlocking(msg, attempt = 0) {
  try {
    // A turn with tool calls can take 10–20 s: never let the default 30 s axios timeout
    // (or a flaky mobile connection) turn a good reply into "something went wrong".
    const { data } = await axios.post('/chat/message/', {
      message: msg, session_key: sessionKey.value, lang: lang.value,
      page: (typeof window !== 'undefined' ? window.location.pathname : ''),
      cart: cartSnapshot(), delivery: readDelivery(), weekly: weeklySnapshot(),
    }, { timeout: 90000 });
    finishReply(null, data);
  } catch (e) {
    const status = e.response && e.response.status;
    if (!status && attempt === 0) { await sendBlocking(msg, 1); return; }   // network blip: one retry
    errorReply(status === 429);
  }
}

// Resolve the absolute stream URL from axios's configured base (prod = api.chds.com.au).
function streamUrl() {
  const base = (axios.defaults.baseURL || '/api').replace(/\/$/, '');
  return `${base}/chat/stream/`;
}

// Streaming turn via SSE. Renders tokens progressively into one assistant bubble; on the
// closing `final` event attaches cards/chips/cart actions. Any failure (no SSE support,
// network error, non-OK response, or nothing streamed) falls back to the blocking path.
async function sendStream(msg) {
  if (typeof fetch !== 'function' || typeof ReadableStream === 'undefined') {
    await sendBlocking(msg);
    return;
  }
  const bubble = reactive({ role: 'assistant', text: '', cards: [], quick: [], streaming: true });
  let started = false;   // a bubble was shown to the user
  let finished = false;  // a `final` event completed the turn
  try {
    const headers = { 'Content-Type': 'application/json', Accept: 'text/event-stream' };
    const auth = useAuthStore();
    if (auth && auth.accessToken) headers.Authorization = `Bearer ${auth.accessToken}`;
    const resp = await fetch(streamUrl(), {
      method: 'POST',
      headers,
      body: JSON.stringify({
        message: msg, session_key: sessionKey.value, lang: lang.value,
        page: (typeof window !== 'undefined' ? window.location.pathname : ''),
        cart: cartSnapshot(), delivery: readDelivery(), weekly: weeklySnapshot(),
      }),
    });
    if (!resp.ok || !resp.body) {
      if (resp.status === 429) { errorReply(true); return; }
      await sendBlocking(msg);
      return;
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      // SSE frames are separated by a blank line.
      let idx;
      while ((idx = buf.indexOf('\n\n')) !== -1) {
        const frame = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        let event = 'message'; let dataStr = '';
        for (const line of frame.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) dataStr += line.slice(5).trim();
        }
        if (!dataStr) continue;
        let data; try { data = JSON.parse(dataStr); } catch (e) { continue; }
        if (event === 'delta') {
          if (!started) { messages.value.push(bubble); started = true; streamingActive.value = true; }
          bubble.text += data.text || '';
          scrollSoon();
        } else if (event === 'reset') {
          bubble.text = '';
        } else if (event === 'final') {
          if (!started) { messages.value.push(bubble); started = true; streamingActive.value = true; }
          finishReply(bubble, data);
          finished = true;
        }
      }
    }
    if (!finished) {
      // Stream ended without a final event — recover via the blocking path.
      if (started) { messages.value.pop(); }
      await sendBlocking(msg);
    }
  } catch (e) {
    if (started && !finished) { messages.value.pop(); }
    if (!finished) await sendBlocking(msg);
  }
}

async function send(text) {
  const msg = (text != null ? text : input.value).trim();
  if (!msg || sending.value) return;
  input.value = '';
  messages.value.push({ role: 'user', text: msg });
  sending.value = true;
  scrollSoon();
  try {
    await sendStream(msg);
  } finally { sending.value = false; streamingActive.value = false; scrollSoon(); persist(); }
}

async function rateReply(m, rating) {
  if (m.rated) return;
  m.rated = true;
  try {
    await axios.post('/chat/feedback/', {
      session_key: sessionKey.value, source: 'chat_rating', category: 'bot', rating,
    });
  } catch (e) { /* non-critical */ }
}

async function submitContact() {
  contactErr.value = '';
  if (!contact.phone.trim() && !contact.email.trim()) {
    contactErr.value = lang.value === 'zh' ? '请至少留下电话或邮箱' : 'Please leave a phone number or email.';
    return;
  }
  contactSending.value = true;
  try {
    await axios.post('/chat/contact/', {
      session_key: sessionKey.value,
      name: contact.name, phone: contact.phone, email: contact.email,
    });
    contactSent.value = true;
    askContact.value = false;
    try { localStorage.setItem(CONTACT_LS, '1'); } catch (e) { /* noop */ }
    messages.value.push({ role: 'assistant', text: lang.value === 'zh'
      ? '收到！我们的团队会尽快通过您留下的方式联系您 🙏'
      : 'Got it — our team will get back to you shortly. Thank you! 🙏' });
    scrollSoon();
  } catch (e) {
    contactErr.value = lang.value === 'zh' ? '提交失败，请重试。' : 'Could not send — please try again.';
  } finally {
    contactSending.value = false;
  }
}

const ui = computed(() => (lang.value === 'zh'
  ? { placeholder: '输入消息…', cart: '查看购物车', title: '在线 · 秒回', items: '件商品', add: '加入', added: '已加入 ✓', popular: '热门', details: '详情', see: '查看餐盒', seePkg: '去选套餐', online: '在线', yourCart: '您的购物车', emptyCart: '购物车还是空的', subtotal: '小计', proceed: '去结账', backToChat: '返回对话' }
  : { placeholder: 'Type a message…', cart: 'View Cart', title: 'Online · replies in seconds', items: 'items', add: 'Add', added: 'Added ✓', popular: 'Popular', details: 'Details', see: 'See boxes', seePkg: 'Build a package', online: 'Online', yourCart: 'Your Cart', emptyCart: 'Your cart is empty', subtotal: 'Subtotal', proceed: 'Proceed to Checkout', backToChat: 'Back to chat' }));

// SPA navigation keeps the widget mounted (it lives outside <router-view>), so
// the conversation stays intact when the bot sends the customer to another page.
// On mobile the panel is full-screen, so close it after navigating — otherwise it
// covers the page they were sent to (the conversation is saved and reopens intact).
function navTo(path) {
  persist();
  if (window.innerWidth <= 560) { open.value = false; persist(); }
  router.push(path).catch(() => {});
}
function goBox(link) { navTo(link || '/box'); }
const cartTotal = computed(() => Number(cart.TotalOrderPrice || 0).toFixed(2));

// In-chat cart review — "View Cart" opens a cart panel inside the widget (see items,
// change qty, remove) with a clear "Proceed to Checkout" when they're ready.
const cartView = ref(false);
function openCart() { cartView.value = true; }
function closeCart() { cartView.value = false; }
function itemName(it) { return it.meal_trans_code ? tr.translate(it.meal_trans_code, ['menu_item'], it.meal_name) : it.meal_name; }
function incItem(id) { cart.increaseItemQuantity(id); }
function decItem(id) { cart.decreaseItemQuantity(id); }
function removeItem(id) { cart.removeFromCart(id); if (!cart.cart.length) cartView.value = false; }
function goCheckout() { cartView.value = false; navTo(weeklyOn.value ? '/checkout-week' : '/checkout'); }
</script>

<template>
  <div v-if="enabled" v-show="!bsOverlays" class="chw" :class="{ 'chw-lift': onBarPage }">
    <transition name="chw-pop">
      <div v-if="open" class="chw-panel" role="dialog" aria-label="Chat">
        <div class="chw-head">
          <div class="chw-avatar">🥢</div>
          <div class="chw-head-txt">
            <div class="chw-name">{{ botName }}</div>
            <div class="chw-status"><span class="chw-dot"></span>{{ ui.title }}</div>
          </div>
          <button class="chw-x" @click="toggle" aria-label="Close">✕</button>
        </div>

        <div class="chw-body" ref="scroller">
          <!-- Returning visitor: resume the earlier chat or start fresh -->
          <div v-if="resumable && !messages.length" class="chw-resume">
            <div class="chw-resume-av">🥢</div>
            <div class="chw-resume-title">{{ lang === 'zh' ? '欢迎回来 👋' : 'Welcome back 👋' }}</div>
            <div class="chw-resume-sub">{{ lang === 'zh' ? '您之前有一段对话，要继续吗？' : 'You have a chat from earlier.' }}</div>
            <button class="chw-resume-btn primary" @click="resumeChat">{{ lang === 'zh' ? '继续之前的对话' : 'Continue previous chat' }}</button>
            <button class="chw-resume-btn" @click="newChat">{{ lang === 'zh' ? '开始新对话' : 'Start a new chat' }}</button>
          </div>
          <template v-for="(m, i) in messages" :key="i">
            <div class="chw-row" :class="m.role">
              <div v-if="m.text" class="chw-bwrap" :class="m.role">
                <div v-if="m.role === 'assistant'" class="chw-msg-av">🥢</div>
                <div class="chw-bubble" :class="m.role"><span v-html="render(m.text)"></span></div>
              </div>
              <div v-if="m.role === 'assistant' && m.text && i > 0" class="chw-rate">
                <span v-if="m.rated" class="chw-rated">{{ lang === 'zh' ? '谢谢反馈 🙏' : 'Thanks 🙏' }}</span>
                <template v-else>
                  <button class="chw-thumb" @click="rateReply(m, 5)" aria-label="Helpful">👍</button>
                  <button class="chw-thumb" @click="rateReply(m, 1)" aria-label="Not helpful">👎</button>
                </template>
              </div>
            </div>

            <!-- Dish / deal cards -->
            <div v-if="m.cards && m.cards.length" class="chw-cards">
              <div v-for="(c, ci) in m.cards" :key="ci" class="chw-card">
                <template v-if="c.type === 'dish'">
                  <img v-if="c.image" :src="c.image" class="chw-card-img" alt="" />
                  <div class="chw-card-body">
                    <div class="chw-card-name">{{ c.name }}<span v-if="c.popular" class="chw-badge">{{ ui.popular }}</span></div>
                    <div v-if="c.weekly && c.date_label" class="chw-datechip">📅 {{ c.date_label }}</div>
                    <div v-if="c.desc" class="chw-card-desc">{{ c.desc }} <a v-if="c.link" href="#" class="chw-more" @click.prevent="navTo(c.link)">{{ ui.details }}</a></div>
                    <div class="chw-variants">
                      <button v-for="(v, vi) in c.variants" :key="vi" class="chw-vbtn" :class="{ done: addedIds[v.portion_price_id] }" @click="addVariant(c, v)">
                        <span class="chw-vsize">{{ v.size }}</span>
                        <span class="chw-vprice">A${{ Number(v.price).toFixed(0) }}</span>
                        <span class="chw-vadd">{{ addedIds[v.portion_price_id] ? ui.added : ('+ ' + ui.add) }}</span>
                      </button>
                    </div>
                  </div>
                </template>
                <template v-else-if="c.type === 'box'">
                  <div class="chw-card-body chw-box">
                    <div class="chw-card-name">📦 {{ c.name }}</div>
                    <div class="chw-card-desc"><template v-if="c.sub">{{ c.sub }}</template><template v-else>{{ c.meals }} {{ ui.items }}</template><span v-if="c.price"> · A${{ Number(c.price).toFixed(0) }}</span><span v-if="c.saves" class="chw-save"> · save A${{ Number(c.saves).toFixed(0) }}</span></div>
                    <button class="chw-vbtn solo" @click="goBox(c.link)">{{ c.link === '/packages' ? ui.seePkg : ui.see }} →</button>
                  </div>
                </template>
              </div>
            </div>

            <!-- Quick replies (only under the latest assistant message) -->
            <div v-if="m.role === 'assistant' && m.quick && m.quick.length && i === messages.length - 1 && !sending" class="chw-quick">
              <button v-for="(q, qi) in m.quick" :key="qi" class="chw-qbtn" @click="send(q)">{{ q }}</button>
            </div>
          </template>

          <div v-if="sending && !streamingActive" class="chw-row assistant">
            <div class="chw-bubble assistant chw-typing"><span></span><span></span><span></span></div>
          </div>
        </div>

        <!-- Opening suggestion chips -->
        <div v-if="chips.length && messages.length <= 1" class="chw-chips">
          <button v-for="(c, i) in chips" :key="i" class="chw-chip" @click="send(c)">{{ c }}</button>
        </div>

        <!-- Live cart bar -->
        <button v-if="weeklyOn && wk.lineCount > 0" class="chw-cartbar" @click="navTo('/checkout-week')">
          <span class="chw-cartcount">🛒 {{ wk.lineCount }} {{ ui.items }}</span>
          <span class="chw-cartmid">{{ wk.quote ? 'A$' + wk.quote.totals.total : '' }}</span>
          <span class="chw-cartgo">{{ ui.proceed }} →</span>
        </button>
        <button v-else-if="!weeklyOn && cart.totalQty > 0" class="chw-cartbar" @click="openCart">
          <span class="chw-cartcount">🛒 {{ cart.totalQty }} {{ ui.items }}</span>
          <span class="chw-cartmid">A${{ cartTotal }}</span>
          <span class="chw-cartgo">{{ ui.cart }} →</span>
        </button>

        <!-- Human handoff: collect a guest's contact details for follow-up -->
        <div v-if="askContact" class="chw-contact">
          <div class="chw-contact-h">{{ lang === 'zh' ? '留下联系方式，我们的团队会尽快联系您 👋' : 'Leave your details and our team will get back to you 👋' }}</div>
          <input v-model="contact.name" class="chw-cf" :placeholder="lang === 'zh' ? '称呼（选填）' : 'Name (optional)'" autocomplete="name" />
          <input v-model="contact.phone" class="chw-cf" :placeholder="lang === 'zh' ? '电话 / 微信' : 'Phone / WeChat'" autocomplete="tel" />
          <input v-model="contact.email" class="chw-cf" type="email" :placeholder="lang === 'zh' ? '邮箱（选填）' : 'Email (optional)'" autocomplete="email" />
          <div v-if="contactErr" class="chw-cerr">{{ contactErr }}</div>
          <div class="chw-crow">
            <button class="chw-cbtn" :disabled="contactSending" @click="submitContact">{{ contactSending ? '…' : (lang === 'zh' ? '发送给团队' : 'Send to team') }}</button>
            <button class="chw-cskip" @click="askContact = false">{{ lang === 'zh' ? '暂不' : 'Not now' }}</button>
          </div>
        </div>

        <form class="chw-input" @submit.prevent="send()">
          <input v-model="input" :placeholder="ui.placeholder" :disabled="sending" autocomplete="off" />
          <button type="submit" :disabled="sending || !input.trim()" aria-label="Send">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2z"/></svg>
          </button>
        </form>

        <!-- Cart review (in-chat) -->
        <div v-if="cartView" class="chw-cartview">
          <div class="chw-cart-head">
            <button class="chw-cart-back" @click="closeCart" aria-label="Back">‹</button>
            <span class="chw-cart-title">{{ ui.yourCart }}</span>
            <button class="chw-x2" @click="closeCart" aria-label="Close">✕</button>
          </div>
          <div class="chw-cart-list">
            <div v-if="!cart.cart.length" class="chw-cart-empty">{{ ui.emptyCart }}</div>
            <div v-for="it in cart.cart" :key="it.id" class="chw-ci">
              <div class="chw-ci-main">
                <div class="chw-ci-name">{{ itemName(it) }}</div>
                <div class="chw-ci-size">{{ it.selected_meal_portion_name }}</div>
                <div class="chw-ci-qty">
                  <button type="button" @click="decItem(it.id)" aria-label="Less">−</button>
                  <span>{{ it.quantity }}</span>
                  <button type="button" @click="incItem(it.id)" aria-label="More">+</button>
                </div>
              </div>
              <div class="chw-ci-side">
                <div class="chw-ci-price">A${{ Number(it.total_price).toFixed(2) }}</div>
                <button class="chw-ci-rm" type="button" @click="removeItem(it.id)" aria-label="Remove">✕</button>
              </div>
            </div>
          </div>
          <div class="chw-cart-foot">
            <div class="chw-cart-sub"><span>{{ ui.subtotal }}</span><span>A${{ cartTotal }}</span></div>
            <button class="chw-cart-checkout" type="button" :disabled="!cart.totalQty" @click="goCheckout">{{ ui.proceed }} →</button>
            <button class="chw-cart-back2" type="button" @click="closeCart">← {{ ui.backToChat }}</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Proactive teaser pop-up -->
    <transition name="chw-teaser-pop">
      <div v-if="teaser && !open" class="chw-teaser" @click="openFromTeaser">
        <div class="chw-teaser-av">🥢</div>
        <div class="chw-teaser-txt">{{ teaserText }}</div>
        <button class="chw-teaser-x" @click.stop="dismissTeaser" aria-label="Dismiss">✕</button>
      </div>
    </transition>

    <div class="chw-launch">
      <span v-if="!open" class="chw-ring"></span>
      <button class="chw-fab" :class="{ open }" @click="toggle" aria-label="Chat with us">
        <svg v-if="!open" viewBox="0 0 24 24" width="27" height="27" fill="currentColor"><path d="M12 3C6.5 3 2 6.8 2 11.5c0 2.3 1.1 4.4 2.9 5.9L4 21l4.2-1.7c1.2.3 2.5.5 3.8.5 5.5 0 10-3.8 10-8.5S17.5 3 12 3z"/></svg>
        <svg v-else viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M18.3 5.7L12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7l1.4-1.4L10.6 10.6l6.3-6.3z"/></svg>
        <span v-if="teaser && !open" class="chw-fab-dot"></span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.chw { position: fixed; right: 18px; bottom: 18px; z-index: 1200; font-family: inherit; }
/* On pages with a full-width fixed bottom bar (Home / OrderNow), lift the launcher above
   it on tablet + mobile so it never covers the cart/order button. */
@media (max-width: 991px) { .chw.chw-lift { right: 14px; bottom: calc(84px + env(safe-area-inset-bottom, 0px)); } }
.chw-launch { position: relative; width: 62px; height: 62px; margin-left: auto; }
.chw-fab { position: relative; width: 62px; height: 62px; border-radius: 50%; border: none; cursor: pointer; background: linear-gradient(145deg, #4f7a3e, #35502a); color: #fff; box-shadow: 0 10px 26px rgba(53,80,42,.45); display: flex; align-items: center; justify-content: center; transition: transform .18s, box-shadow .2s; }
.chw-fab:hover { transform: scale(1.07); box-shadow: 0 12px 30px rgba(53,80,42,.55); }
.chw-fab:active { transform: scale(.98); }
.chw-fab.open { background: linear-gradient(145deg, #3c5f2b, #2c4322); }
.chw-ring { position: absolute; inset: 0; border-radius: 50%; background: #446936; opacity: .35; animation: chw-ring 2.4s ease-out infinite; z-index: -1; }
@keyframes chw-ring { 0% { transform: scale(1); opacity: .35; } 70% { transform: scale(1.55); opacity: 0; } 100% { opacity: 0; } }
.chw-fab-dot { position: absolute; top: 3px; right: 3px; width: 13px; height: 13px; border-radius: 50%; background: #ff5a5a; border: 2px solid #fff; }
.chw-datechip { display: inline-block; font-size: 11px; font-weight: 700; color: #2f5018; background: #eef4e2; border-radius: 999px; padding: 2px 8px; margin: 2px 0 4px; }
.chw-more { color: #446936; font-weight: 700; text-decoration: none; margin-left: 4px; }
.chw-panel { position: absolute; right: 0; bottom: 74px; width: 380px; max-width: calc(100vw - 24px); height: 580px; max-height: calc(100vh - 110px); background: #fff; border-radius: 18px; overflow: hidden; box-shadow: 0 18px 50px rgba(0,0,0,.28); display: flex; flex-direction: column; }
.chw-head { display: flex; align-items: center; gap: 11px; padding: 13px 15px; background: linear-gradient(135deg, #4f7a3e, #34502a); color: #fff; box-shadow: 0 2px 10px rgba(0,0,0,.12); }
.chw-avatar { width: 40px; height: 40px; border-radius: 50%; background: rgba(255,255,255,.16); border: 1.5px solid rgba(255,255,255,.35); display: flex; align-items: center; justify-content: center; font-size: 21px; flex-shrink: 0; }
.chw-head-txt { flex: 1; line-height: 1.3; }
.chw-name { font-weight: 700; font-size: 15px; }
.chw-status { font-size: 11.5px; opacity: .9; display: flex; align-items: center; gap: 5px; }
.chw-dot { width: 7px; height: 7px; border-radius: 50%; background: #B6D452; animation: chw-pulse 2s infinite; }
@keyframes chw-pulse { 0% { box-shadow: 0 0 0 0 rgba(182,212,82,.6); } 70% { box-shadow: 0 0 0 6px rgba(182,212,82,0); } }
.chw-x { background: rgba(255,255,255,.12); border: none; color: #fff; font-size: 14px; cursor: pointer; opacity: .9; width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
.chw-x:hover { background: rgba(255,255,255,.24); }

.chw-body { flex: 1; overflow-y: auto; overflow-x: hidden; -webkit-overflow-scrolling: touch; overscroll-behavior: contain; padding: 14px; background: linear-gradient(#f3f5ee, #f5f6f2); display: flex; flex-direction: column; gap: 9px; }
.chw-resume { margin: auto; text-align: center; background: #fff; border: 1px solid #e9ece2; border-radius: 16px; padding: 22px 18px; box-shadow: 0 2px 10px rgba(0,0,0,.06); max-width: 280px; }
.chw-resume-av { width: 46px; height: 46px; margin: 0 auto 10px; border-radius: 50%; background: linear-gradient(145deg,#5a874a,#3c5a2e); display: flex; align-items: center; justify-content: center; font-size: 24px; }
.chw-resume-title { font-weight: 700; font-size: 16px; color: #26331d; }
.chw-resume-sub { font-size: 13px; color: #7a8071; margin: 5px 0 16px; }
.chw-resume-btn { display: block; width: 100%; border: 1px solid #d7dccd; background: #fff; color: #3c5a2e; border-radius: 22px; padding: 10px; font-size: 13.5px; font-weight: 600; cursor: pointer; margin-top: 8px; }
.chw-resume-btn.primary { background: linear-gradient(145deg,#4f7a3e,#446936); color: #fff; border-color: #446936; }
.chw-resume-btn:not(.primary):hover { background: #f2f7ea; }
.chw-row { display: flex; flex-direction: column; }
.chw-row.user { align-items: flex-end; }
.chw-row.assistant { align-items: flex-start; }
.chw-bwrap { display: flex; align-items: flex-end; gap: 7px; max-width: 90%; }
.chw-bwrap.user { flex-direction: row-reverse; }
.chw-msg-av { width: 26px; height: 26px; min-width: 26px; border-radius: 50%; background: linear-gradient(145deg,#5a874a,#3c5a2e); display: flex; align-items: center; justify-content: center; font-size: 14px; margin-bottom: 2px; box-shadow: 0 1px 3px rgba(0,0,0,.12); }
.chw-bubble { padding: 9px 13px; border-radius: 16px; font-size: 14px; line-height: 1.46; word-wrap: break-word; overflow-wrap: anywhere; }
.chw-bubble.assistant { background: #fff; color: #26331d; border-bottom-left-radius: 5px; box-shadow: 0 1px 4px rgba(0,0,0,.08); }
.chw-bubble.user { background: linear-gradient(145deg,#4f7a3e,#446936); color: #fff; border-bottom-right-radius: 5px; box-shadow: 0 1px 4px rgba(68,105,54,.25); }

/* Teaser pop-up */
.chw-teaser { position: absolute; right: 4px; bottom: 76px; width: 234px; display: flex; align-items: center; gap: 9px; background: #fff; border-radius: 16px; padding: 11px 12px; box-shadow: 0 10px 30px rgba(0,0,0,.2); cursor: pointer; border: 1px solid #eceee7; }
.chw-teaser::after { content: ''; position: absolute; right: 22px; bottom: -7px; width: 14px; height: 14px; background: #fff; border-right: 1px solid #eceee7; border-bottom: 1px solid #eceee7; transform: rotate(45deg); }
.chw-teaser:hover { box-shadow: 0 12px 34px rgba(0,0,0,.26); }
.chw-teaser-av { width: 34px; height: 34px; min-width: 34px; border-radius: 50%; background: linear-gradient(145deg,#5a874a,#3c5a2e); display: flex; align-items: center; justify-content: center; font-size: 18px; }
.chw-teaser-txt { font-size: 13px; line-height: 1.35; color: #2b3a21; font-weight: 600; }
.chw-teaser-x { position: absolute; top: 5px; right: 6px; background: none; border: none; color: #b6bbad; font-size: 11px; cursor: pointer; padding: 2px; line-height: 1; }
.chw-teaser-x:hover { color: #7a8071; }
.chw-teaser-pop-enter-active, .chw-teaser-pop-leave-active { transition: opacity .25s, transform .25s; transform-origin: bottom right; }
.chw-teaser-pop-enter-from, .chw-teaser-pop-leave-to { opacity: 0; transform: translateY(8px) scale(.9); }

/* Cards */
.chw-cards { display: flex; flex-direction: column; gap: 8px; margin: 2px 0; }
.chw-card { display: flex; background: #fff; border: 1px solid #e9ece2; border-radius: 13px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,.06); }
.chw-card-img { width: 82px; min-width: 82px; height: auto; object-fit: cover; }
.chw-card-body { padding: 9px 11px; flex: 1; min-width: 0; }
.chw-box { padding: 11px 12px; }
.chw-card-name { font-weight: 700; font-size: 13.5px; color: #26331d; display: flex; align-items: center; gap: 6px; }
.chw-badge { background: #eaf3d8; color: #4a6b1f; font-size: 10px; font-weight: 700; border-radius: 9px; padding: 1px 7px; }
.chw-card-desc { font-size: 11.5px; color: #7a8071; margin: 3px 0 7px; line-height: 1.35; }
.chw-save { color: #b23522; font-weight: 700; }
.chw-variants { display: flex; flex-wrap: wrap; gap: 6px; }
.chw-vbtn { display: flex; align-items: center; gap: 6px; background: #f4f7ee; border: 1px solid #d7e3bf; color: #3c5a2e; border-radius: 18px; padding: 5px 10px; font-size: 12px; cursor: pointer; font-family: inherit; transition: background .15s; }
.chw-vbtn:hover { background: #e7f0d5; }
.chw-vbtn.done { background: #446936; color: #fff; border-color: #446936; }
.chw-vbtn.solo { background: #446936; color: #fff; border-color: #446936; margin-top: 8px; font-weight: 600; }
.chw-vsize { font-weight: 600; }
.chw-vprice { opacity: .85; }
.chw-vadd { font-weight: 700; }

/* Quick replies */
.chw-quick { display: flex; flex-wrap: wrap; gap: 6px; margin: 4px 0 2px; }
.chw-qbtn { background: #fff; border: 1px solid #cdd7c0; color: #3c5a2e; border-radius: 16px; padding: 6px 12px; font-size: 12.5px; cursor: pointer; }
.chw-qbtn:hover { background: #eaf3d8; border-color: #B6D452; }

.chw-typing { display: flex; gap: 4px; align-items: center; }
.chw-typing span { width: 7px; height: 7px; border-radius: 50%; background: #b9bfb0; animation: chw-bounce 1.2s infinite; }
.chw-typing span:nth-child(2) { animation-delay: .18s; }
.chw-typing span:nth-child(3) { animation-delay: .36s; }
@keyframes chw-bounce { 0%, 60%, 100% { transform: translateY(0); opacity: .5; } 30% { transform: translateY(-5px); opacity: 1; } }

.chw-chips { display: flex; flex-wrap: wrap; gap: 7px; padding: 0 14px 10px; background: #f5f6f2; }
.chw-chip { background: #fff; border: 1px solid #dbe0d3; color: #3c5a2e; border-radius: 18px; padding: 7px 12px; font-size: 12.5px; cursor: pointer; }
.chw-chip:hover { background: #eaf3d8; border-color: #B6D452; }

.chw-cartbar { display: flex; align-items: center; gap: 8px; width: 100%; border: none; cursor: pointer; background: #446936; color: #fff; padding: 11px 16px; font-size: 13.5px; font-weight: 700; }
.chw-cartbar:hover { background: #3c5f2b; }
.chw-cartcount { flex: 1; text-align: left; }
.chw-cartmid { opacity: .85; }
.chw-cartgo { background: rgba(255,255,255,.2); border-radius: 14px; padding: 3px 11px; }

/* In-chat cart review */
.chw-cartview { position: absolute; inset: 0; z-index: 6; background: #f5f6f2; display: flex; flex-direction: column; }
.chw-cart-head { display: flex; align-items: center; gap: 8px; padding: 13px 14px; background: linear-gradient(135deg, #4f7a3e, #34502a); color: #fff; }
.chw-cart-back { background: rgba(255,255,255,.14); border: none; color: #fff; width: 30px; height: 30px; border-radius: 50%; font-size: 22px; line-height: 1; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0 0 3px; }
.chw-cart-title { flex: 1; font-weight: 700; font-size: 15px; }
.chw-x2 { background: rgba(255,255,255,.12); border: none; color: #fff; width: 28px; height: 28px; border-radius: 50%; font-size: 14px; cursor: pointer; }
.chw-cart-list { flex: 1; overflow-y: auto; overscroll-behavior: contain; padding: 12px; display: flex; flex-direction: column; gap: 9px; }
.chw-cart-empty { text-align: center; color: #9aa093; padding: 30px 10px; font-size: 14px; }
.chw-ci { display: flex; justify-content: space-between; gap: 10px; background: #fff; border: 1px solid #eaece4; border-radius: 12px; padding: 10px 12px; }
.chw-ci-main { min-width: 0; flex: 1; }
.chw-ci-name { font-weight: 700; font-size: 13.5px; color: #26331d; }
.chw-ci-size { font-size: 11.5px; color: #8a908a; margin: 2px 0 8px; }
.chw-ci-qty { display: inline-flex; align-items: center; gap: 0; border: 1px solid #d7e0cb; border-radius: 18px; overflow: hidden; }
.chw-ci-qty button { width: 28px; height: 28px; border: none; background: #f2f7ea; color: #3c5a2e; font-size: 16px; cursor: pointer; }
.chw-ci-qty button:hover { background: #e6f0d5; }
.chw-ci-qty span { min-width: 26px; text-align: center; font-size: 13px; font-weight: 600; color: #26331d; }
.chw-ci-side { display: flex; flex-direction: column; align-items: flex-end; justify-content: space-between; }
.chw-ci-price { font-weight: 700; font-size: 13.5px; color: #446936; }
.chw-ci-rm { background: none; border: none; color: #c3c8bc; font-size: 13px; cursor: pointer; padding: 2px; }
.chw-ci-rm:hover { color: #c0392b; }
.chw-cart-foot { border-top: 1px solid #e6e9df; background: #fff; padding: 12px 14px calc(12px + env(safe-area-inset-bottom, 0px)); }
.chw-cart-sub { display: flex; justify-content: space-between; font-weight: 800; color: #26331d; font-size: 15px; margin-bottom: 10px; }
.chw-cart-checkout { width: 100%; border: none; background: linear-gradient(145deg,#4f7a3e,#446936); color: #fff; font-weight: 700; font-size: 14.5px; padding: 12px; border-radius: 12px; cursor: pointer; }
.chw-cart-checkout:disabled { background: #b7bfad; cursor: default; }
.chw-cart-checkout:not(:disabled):hover { background: #3c5f2b; }
.chw-cart-back2 { width: 100%; border: none; background: none; color: #6b7266; font-size: 12.5px; cursor: pointer; padding: 9px 0 2px; }
.chw-cart-back2:hover { color: #446936; }

.chw-rate { display: flex; align-items: center; gap: 4px; margin: 1px 0 2px 34px; }
.chw-thumb { background: none; border: none; cursor: pointer; font-size: 13px; opacity: 0.5; padding: 2px 3px; line-height: 1; transition: opacity .15s, transform .15s; }
.chw-thumb:hover { opacity: 1; transform: scale(1.15); }
.chw-rated { font-size: 11px; color: #7d9350; }
.chw-contact { padding: 12px; border-top: 1px solid #e6e9df; background: #f4f8ec; display: flex; flex-direction: column; gap: 7px; }
.chw-contact-h { font-size: 13px; font-weight: 600; color: #2b3a22; line-height: 1.35; }
.chw-cf { border: 1px solid #cdd9b3; border-radius: 10px; padding: 9px 12px; font-size: 16px; outline: none; background: #fff; }
.chw-cf:focus { border-color: #446936; }
.chw-cerr { font-size: 12px; color: #b23c2e; }
.chw-crow { display: flex; gap: 8px; align-items: center; margin-top: 2px; }
.chw-cbtn { flex: 1; background: #446936; color: #fff; border: none; border-radius: 10px; padding: 10px; font-weight: 700; font-size: 14px; cursor: pointer; }
.chw-cbtn:disabled { background: #b7bfad; cursor: default; }
.chw-cskip { background: none; border: none; color: #6b7b56; font-size: 13px; cursor: pointer; padding: 6px 10px; }
.chw-input { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-top: 1px solid #e6e9df; background: #fff; }
.chw-input input { flex: 1; min-width: 0; border: 1px solid #d7dccd; border-radius: 22px; padding: 10px 14px; font-size: 16px; outline: none; }
.chw-input input:focus { border-color: #446936; }
.chw-input button { width: 40px; height: 40px; border-radius: 50%; border: none; background: #446936; color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.chw-input button:disabled { background: #b7bfad; cursor: default; }

.chw-pop-enter-active, .chw-pop-leave-active { transition: opacity .18s, transform .18s; transform-origin: bottom right; }
.chw-pop-enter-from, .chw-pop-leave-to { opacity: 0; transform: scale(.92) translateY(10px); }

@media (max-width: 560px) {
  /* Lift the launcher + teaser above the page's fixed bottom bar (cart/menu). */
  .chw { right: 14px; }
  /* Full-screen chat, like a native mobile messenger — no page overlap.
     Height tracks the VISUAL viewport (--chw-vh, set in JS) so the input stays
     above the on-screen keyboard instead of hiding behind it. */
  .chw-panel {
    /* top tracks visualViewport.offsetTop so the panel stays aligned when the
       keyboard makes iOS offset the visual viewport (see syncViewport). */
    position: fixed; top: var(--chw-top, 0px); left: 0; right: 0; bottom: auto;
    width: 100%; max-width: 100%; border-radius: 0;
    height: var(--chw-vh, 100dvh); max-height: var(--chw-vh, 100dvh);
    overflow-x: hidden; overscroll-behavior: none;
  }
  .chw-head { padding-top: calc(13px + env(safe-area-inset-top, 0px)); }
  .chw-input { padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px)); }
  /* When the full-screen panel is open, the header ✕ closes it — hide the FAB. */
  .chw-fab.open { display: none; }
  .chw-teaser { right: 0; width: 220px; }
}
</style>
